/**
 * Deterministic pseudo-randomness from a string.
 *
 * Avatars must be stable: the same player gets the same mark every time they
 * appear, in every client, without the server sending artwork. Seeding from
 * their id gives that for free.
 *
 * Distinct from the server's seeded generator, which exists so gameplay
 * randomness is reproducible in tests. This one never leaves the browser and
 * decides nothing about the game.
 */

/** A fresh avatar seed. The only place in the client that is not deterministic. */
export const randomSeed = (): string => Math.random().toString(36).slice(2, 10);

/** FNV-1a. Small, fast, good enough spread for picking indices. */
const hash = (input: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

/** mulberry32 — tiny, well-distributed, entirely adequate for picking shapes. */
export const seeded = (seed: string) => {
  let state = hash(seed);

  const next = () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  return {
    next,
    /** Integer in [0, max). */
    int: (max: number) => Math.floor(next() * max),
    pick: <T>(items: readonly T[]): T => items[Math.floor(next() * items.length)],
    /** Float in [min, max). */
    range: (min: number, max: number) => min + next() * (max - min),
  };
};

/** Relative luminance, for choosing readable ink over a generated fill. */
export const isLight = (hex: string): boolean => {
  const h = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) > 0.35;
};

/**
 * A player's name colour, derived from the same seed as their avatar so the two
 * always agree and neither has to be transferred.
 */
export const nameColorFor = (seed: string, palette: readonly string[]): string =>
  palette[Math.floor(seeded(`name-${seed}`).next() * palette.length)];
